import type { AuditEvent, AuditEventCode } from '@magimp/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../i18n/index.js';
import { DataTable, type Column } from '../../../ui/index.js';

export interface AuditTableProps {
  rows: readonly AuditEvent[];
  onSelect: (event: AuditEvent) => void;
}

export const AUDIT_EVENT_LABEL_KEYS = {
  'user.create': 'events.userCreate',
  'user.update': 'events.userUpdate',
  'user.deactivate': 'events.userDeactivate',
  'user.reactivate': 'events.userReactivate',
  'user.resetPassword': 'events.userResetPassword',
  'user.changePassword': 'events.userChangePassword',
  'user.self-update': 'events.userSelfUpdate',
  'user.shift-toggle': 'events.userShiftToggle',
  'department.create': 'events.departmentCreate',
  'department.update': 'events.departmentUpdate',
  'department.archive': 'events.departmentArchive',
  'department.restore': 'events.departmentRestore',
  'role.create': 'events.roleCreate',
  'role.update': 'events.roleUpdate',
  'role.archive': 'events.roleArchive',
  'role.restore': 'events.roleRestore',
  'role.permissions.set': 'events.rolePermissionsSet',
  'carrier.create': 'events.carrierCreate',
  'carrier.update': 'events.carrierUpdate',
  'carrier.deactivate': 'events.carrierDeactivate',
  'carrier.activate': 'events.carrierActivate',
  'carrier.warehouse.create': 'events.carrierWarehouseCreate',
  'carrier.warehouse.update': 'events.carrierWarehouseUpdate',
  'carrier.warehouse.delete': 'events.carrierWarehouseDelete',
  'carrier.priceList.create': 'events.carrierPriceListCreate',
  'carrier.priceList.update': 'events.carrierPriceListUpdate',
  'carrier.priceList.delete': 'events.carrierPriceListDelete',
  'request.create': 'events.requestCreate',
  'request.update': 'events.requestUpdate',
  'request.delete': 'events.requestDelete',
  'request.transition': 'events.requestTransition',
  'request.winner': 'events.requestWinner',
  'request.carrier.add': 'events.requestCarrierAdd',
  'request.carrier.update': 'events.requestCarrierUpdate',
  'request.carrier.remove': 'events.requestCarrierRemove',
  'request.box.regroup': 'events.requestBoxRegroup',
  'request.chat': 'events.requestChat',
  'request.chat.markUnread': 'events.requestChatMarkUnread',
  'request.chat.unmarkUnread': 'events.requestChatUnmarkUnread',
  'request.draft.idempotent_resolve': 'events.requestDraftIdempotentResolve',
  'request.childRussiaCreated': 'events.requestChildRussiaCreated',
  'client.create': 'events.clientCreate',
  'client.update': 'events.clientUpdate',
  'client.archive': 'events.clientArchive',
  'client.restore': 'events.clientRestore',
  'client.version.restore': 'events.clientVersionRestore',
  'client.contact.add': 'events.clientContactAdd',
  'client.contact.update': 'events.clientContactUpdate',
  'client.contact.archive': 'events.clientContactArchive',
  'client.contact.restore': 'events.clientContactRestore',
  'client.contact.setPrimary': 'events.clientContactSetPrimary',
  'client.legal.add': 'events.clientLegalAdd',
  'client.legal.update': 'events.clientLegalUpdate',
  'client.legal.archive': 'events.clientLegalArchive',
  'client.legal.restore': 'events.clientLegalRestore',
  'client.legal.requisites.add': 'events.clientLegalRequisitesAdd',
  'client.legal.requisites.update': 'events.clientLegalRequisitesUpdate',
  'client.legal.requisites.archive': 'events.clientLegalRequisitesArchive',
  'client.legal.requisites.restore': 'events.clientLegalRequisitesRestore',
  'transportType.create': 'events.transportTypeCreate',
  'transportType.update': 'events.transportTypeUpdate',
  'transportType.archive': 'events.transportTypeArchive',
  'transportType.restore': 'events.transportTypeRestore',
  'arrivalCity.create': 'events.arrivalCityCreate',
  'arrivalCity.update': 'events.arrivalCityUpdate',
  'arrivalCity.archive': 'events.arrivalCityArchive',
  'arrivalCity.restore': 'events.arrivalCityRestore',
  'roadOption.create': 'events.roadOptionCreate',
  'roadOption.update': 'events.roadOptionUpdate',
  'roadOption.archive': 'events.roadOptionArchive',
  'roadOption.restore': 'events.roadOptionRestore',
  'packagingOption.create': 'events.packagingOptionCreate',
  'packagingOption.update': 'events.packagingOptionUpdate',
  'packagingOption.archive': 'events.packagingOptionArchive',
  'packagingOption.restore': 'events.packagingOptionRestore',
  'russiaCarrier.create': 'events.russiaCarrierCreate',
  'russiaCarrier.update': 'events.russiaCarrierUpdate',
  'russiaCarrier.archive': 'events.russiaCarrierArchive',
  'russiaCarrier.restore': 'events.russiaCarrierRestore',
  'russiaRequest.create': 'events.russiaRequestCreate',
  'russiaRequest.update': 'events.russiaRequestUpdate',
  'russiaRequest.delete': 'events.russiaRequestDelete',
  'russiaRequest.transition': 'events.russiaRequestTransition',
  'russiaRequest.chat': 'events.russiaRequestChat',
  'russiaRequest.chat.markUnread': 'events.russiaRequestChatMarkUnread',
  'russiaRequest.chat.unmarkUnread': 'events.russiaRequestChatUnmarkUnread',
  'cargo.create': 'events.cargoCreate',
  'cargo.update': 'events.cargoUpdate',
  'cargo.delete': 'events.cargoDelete',
  'cargo.import': 'events.cargoImport',
  'cargo.document.add': 'events.cargoDocumentAdd',
  'cargo.document.delete': 'events.cargoDocumentDelete',
  'cargo.payment.set': 'events.cargoPaymentSet',
  'cargo.payment.quickSet': 'events.cargoPaymentQuickSet',
  'cargo.payment.requestStatus': 'events.cargoPaymentRequestStatus',
  'cargo.payment.review': 'events.cargoPaymentReview',
  'cargo.clientTariff.set': 'events.cargoClientTariffSet',
  'cargo.chinaPayment.set': 'events.cargoChinaPaymentSet',
  'cargo.carrierPayment.set': 'events.cargoCarrierPaymentSet',
  'cargo.dictionary.change': 'events.cargoDictionaryChange',
  'leadIntakeDuty.toggle': 'events.leadIntakeDutyToggle',
  'callCenterAgent.bind': 'events.callCenterAgentBind',
  'client.lifecycle.working': 'events.clientLifecycleWorking',
  'cargoProblem.create': 'events.cargoProblemCreate',
  'cargoProblem.update': 'events.cargoProblemUpdate',
  'cargoProblem.transition': 'events.cargoProblemTransition',
  'cargoProblem.delete': 'events.cargoProblemDelete',
  'cargoProblem.comment': 'events.cargoProblemComment',
  'cargoProblem.file.upload': 'events.cargoProblemFileUpload',
  'cargoProblem.assignee.change': 'events.cargoProblemAssigneeChange',
  'cargoProblem.attachment.delete': 'events.cargoProblemAttachmentDelete',
  'cargoProblem.reaction.escalate': 'events.cargoProblemReactionEscalate',
} satisfies Record<AuditEventCode, string>;

export function AuditTable({ rows, onSelect }: AuditTableProps) {
  const { t } = useTranslation('audit');
  const columns = useMemo<Column<AuditEvent>[]>(
    () => [
      {
        key: 'at',
        label: t('fields.time'),
        width: 200,
        render: (r) => formatDateTime(r.at),
        sortable: true,
        sortValue: (r) => r.at,
      },
      {
        key: 'actor',
        label: t('fields.actor'),
        render: (r) => (r.actor ? r.actor.email : t('actorDeleted')),
        sortable: true,
        sortValue: (r) => r.actor?.email ?? '',
      },
      {
        key: 'action',
        label: t('fields.action'),
        sortable: true,
        render: (r) => t(AUDIT_EVENT_LABEL_KEYS[r.action], { defaultValue: r.action }),
        sortValue: (r) => t(AUDIT_EVENT_LABEL_KEYS[r.action], { defaultValue: r.action }),
      },
      {
        key: 'target',
        label: t('fields.target'),
        render: (r) => `${r.targetType}${r.targetId ? `:${r.targetId}` : ''}`,
      },
      { key: 'reqId', label: 'reqId', render: (r) => r.reqId ?? '' },
    ],
    [t],
  );
  return (
    <DataTable<AuditEvent>
      columns={columns}
      rows={rows}
      onSelect={onSelect}
      emptyState={t('empty')}
    />
  );
}
